<template>
  <!-- The body is a column of which only the list scrolls, so the fields and the footer stay put -->
  <Modal
    ref="modalRef"
    position="end"
    width="34rem"
    mobile-layout="fullscreen"
    frameless-body
    content-class="create-portfolio-panel"
    @after-close="reset"
  >
    <template #header>New portfolio</template>

    <template #body>
      <div class="fields">
        <FormField label="Name" :error="form.fieldErrors('name')">
          <Input
            v-model="form.fields.name"
            placeholder="e.g. Northside"
            autocomplete="off"
            data-testid="portfolio-name-input"
          />
        </FormField>

        <FormField v-if="isAdmin" label="Owner" :error="form.fieldErrors('ownerId')">
          <TeamMemberPicker v-model="form.fields.ownerId" :members="members" />
        </FormField>

        <!-- A member's portfolio is their own, so there is nobody to pick -->
        <View v-else direction="column" gap="xs" class="owner-you">
          <Text as="span" variant="label-medium">Owner</Text>
          <View gap="sm" align="center" class="owner-you-name">
            <Avatar :user-name="yourName" show-single-letter size="small" />
            <Text as="span" variant="body-medium">{{ yourName }} (you)</Text>
          </View>
        </View>

        <Text variant="body-small" color="secondary" class="owner-hint">{{ ownerHint }}</Text>
      </div>

      <div class="toolbar">
        <View justify="space-between" align="center" gap="sm">
          <Text id="create-portfolio-properties" as="span" variant="label-medium">Properties</Text>
          <Button v-if="shown.length" variant="ghost" size="small" @click="toggleShown">{{ selectAllLabel }}</Button>
        </View>

        <Input v-model="search" type="search" placeholder="Search by address" aria-label="Search by address">
          <template #prefix>
            <Icon name="search-lg" size="small" class="search-icon" />
          </template>
        </Input>

        <!-- Below a handful of properties the chips would only repeat what the list already shows -->
        <ScrollArea v-if="properties.length > chipsFrom">
          <Chips>
            <Chip
              v-for="chip in chips"
              :key="chip.key"
              :is-active="chip.key === statusFilter"
              @click="statusFilter = chip.key"
            >
              {{ chip.label }}
            </Chip>
          </Chips>
        </ScrollArea>
      </div>

      <div class="list" role="group" aria-labelledby="create-portfolio-properties">
        <template v-for="group in groups" :key="group.key">
          <div class="group-header">
            <Text as="span" variant="label-small" color="secondary" class="group-title">{{ group.title }}</Text>
            <Text
              v-if="group.key !== unassignedPortfolioFilter"
              as="span"
              variant="body-small"
              color="tertiary"
              class="group-note"
            >
              Ticking one moves it here
            </Text>
          </div>

          <!-- The whole row toggles; a <label> can't wrap it, since the name renders a label of its own -->
          <div
            v-for="property in group.properties"
            :key="property.id"
            class="row"
            :class="{ checked: isPicked(property) }"
            @click="onRowClick(property, $event)"
          >
            <Checkbox
              :id="`create-portfolio-${property.id}`"
              :model-value="isPicked(property)"
              :aria-labelledby="`create-portfolio-${property.id}-name`"
              @update:model-value="pick(property, Boolean($event))"
            />
            <PropertyPhotoPreview :image-url="property.coverImage" size="sm" />
            <div class="identity">
              <Text :id="`create-portfolio-${property.id}-name`" as="span" variant="label-medium" class="name">
                {{ property.displayName }}
              </Text>
              <div class="meta">
                <PropertyStatusLabel small :status="property.status" class="status-inline" />
                <Text as="span" variant="body-small" color="secondary" class="figures">{{ figuresOf(property) }}</Text>
                <Text as="span" variant="body-small" color="secondary" class="figures-short">
                  {{ shortFiguresOf(property) }}
                </Text>
              </div>
            </div>
            <PropertyStatusLabel small :status="property.status" class="status-trailing" />
          </div>
        </template>

        <Text v-if="!properties.length" variant="body-small" color="secondary" class="list-note">
          {{ noPropertiesNote }}
        </Text>
        <Text v-else-if="!shown.length" variant="body-small" color="secondary" class="list-note">
          No properties match that.
        </Text>
      </div>
    </template>

    <template #footer>
      <div class="summary">
        <Text variant="label-medium" :color="picked.length ? 'primary' : 'tertiary'">{{ selectedLabel }}</Text>
        <Text v-if="consequence" variant="body-small" color="secondary">{{ consequence }}</Text>
      </div>
      <div class="actions">
        <Button variant="ghost" class="cancel" @click="close">Cancel</Button>
        <Button
          :disabled="!canCreate"
          :loading="form.submitting"
          class="create"
          data-testid="portfolio-save-button"
          @click="create"
        >
          Create portfolio
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, reactive, ref } from 'vue';

import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Chips,
  FormField,
  Icon,
  Input,
  Modal,
  ScrollArea,
  Text,
  View,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import {
  CompanyTeamMemberDto,
  PortfolioDto,
  TeamMemberPicker,
  newPortfolioOwnerId,
  teamMemberName as nameOf,
  unitsForNewPortfolio,
  useCreatePortfolio,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  PropertyListItemDto,
  PropertyStatusLabel,
  portfolioQueryKey,
  propertyRent,
  propertyStatusShortLabel,
  propertySummary,
  unassignedPortfolioFilter,
  usePortfolioQuery,
} from '@tallo/investor/property';
import { PropertyPhotoPreview, PropertyStatus } from '@tallo/property';
import { escapeHtml } from '@tallo/utility';

type StatusFilter = 'all' | typeof unassignedPortfolioFilter | PropertyStatus;

interface Group {
  key: string;
  title: string;
  properties: PropertyListItemDto[];
}

const props = defineProps<{ members: CompanyTeamMemberDto[]; portfolios: PortfolioDto[] }>();
const emit = defineEmits<{ created: [portfolio: PortfolioDto] }>();

// Up to this many properties fit on screen as one list, and a chip row would only repeat it
const chipsFrom = 5;
// What the server accepts; a longer name would come back as a field error after the round trip
const nameMaxLength = 120;

const alertsStore = useAlertsStore();
const queryCache = useQueryCache();
const modalRef = ref<InstanceType<typeof Modal>>();
const search = ref('');
const statusFilter = ref<StatusFilter>('all');
const picked = ref<string[]>([]);

const { investor, isAdmin } = storeToRefs(useInvestorStore());
const { properties: companyProperties } = usePortfolioQuery();
const createPortfolio = useCreatePortfolio();

const viewer = computed(() => ({ investorId: investor.value?.id, isAdmin: isAdmin.value }));
// Its owner runs every unit filed in, so a member is offered only what they run already; the API holds to the same
const properties = computed(() => unitsForNewPortfolio(viewer.value, companyProperties.value, props.portfolios));

const yourName = computed(() => {
  const you = props.members.find((member) => member.isYou);

  return you ? nameOf(you) : (investor.value?.user.name ?? '');
});
const ownerHint = computed(() =>
  isAdmin.value
    ? 'The owner runs every property inside: they can reassign it, move it and change its collaborators. Each property keeps its own manager. Collaborators can be added once it exists.'
    : 'You run every property inside: you can reassign it, move it and change its collaborators. Each property keeps its own manager. Collaborators can be added once it exists.',
);
const noPropertiesNote = computed(() =>
  isAdmin.value
    ? "No properties yet. You can file them here later, from each property's Team tab."
    : "You don't run any properties yet. Once it exists, you can create new listings straight into it.",
);

const fields = reactive({ name: '', ownerId: '' });
const form = useForm(fields, {
  name: {
    required: helpers.withMessage('Give the portfolio a name', required),
    maxLength: helpers.withMessage(`Keep the name under ${nameMaxLength} characters`, maxLength(nameMaxLength)),
  },
  ownerId: { required: helpers.withMessage('Choose an owner', required) },
});

const searched = computed(() => {
  const needle = search.value.trim().toLowerCase();

  return needle
    ? properties.value.filter((property) => property.displayName.toLowerCase().includes(needle))
    : properties.value;
});

// The chips count what the search left, so a narrowed list gets narrowed counts
const chips = computed(() => {
  const statuses = Object.values(PropertyStatus).filter((status) =>
    searched.value.some((property) => property.status === status),
  );

  return [
    { key: 'all' as StatusFilter, label: `All · ${searched.value.length}` },
    {
      key: unassignedPortfolioFilter as StatusFilter,
      label: `Unassigned · ${searched.value.filter((property) => !property.portfolio).length}`,
    },
    ...statuses.map((status) => ({
      key: status as StatusFilter,
      label: `${capitalize(propertyStatusShortLabel(status))} · ${searched.value.filter((property) => property.status === status).length}`,
    })),
  ];
});

const shown = computed(() => {
  switch (statusFilter.value) {
    case 'all':
      return searched.value;

    case unassignedPortfolioFilter:
      return searched.value.filter((property) => !property.portfolio);

    default:
      return searched.value.filter((property) => property.status === statusFilter.value);
  }
});

// Where each property lives today: the loose ones first, then each other portfolio by name, so a
// tick that moves something is visible before it is made
const groups = computed<Group[]>(() => {
  const unassigned = shown.value.filter((property) => !property.portfolio);
  const result: Group[] = unassigned.length
    ? [{ key: unassignedPortfolioFilter, title: `Unassigned · ${unassigned.length}`, properties: unassigned }]
    : [];

  const filed = [...props.portfolios]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((portfolio) => ({
      portfolio,
      properties: shown.value.filter((property) => property.portfolio?.id === portfolio.id),
    }))
    .filter(({ properties }) => properties.length);

  for (const { portfolio, properties } of filed) {
    const owner = props.members.find((member) => member.investorId === portfolio.ownerId);

    result.push({
      key: portfolio.id,
      title: [portfolio.name, owner ? nameOf(owner) : undefined, properties.length].filter(Boolean).join(' · '),
      properties,
    });
  }

  return result;
});

const isNarrowed = computed(() => shown.value.length !== properties.value.length);
const allShownPicked = computed(() => shown.value.every((property) => picked.value.includes(property.id)));
const selectAllLabel = computed(() =>
  allShownPicked.value ? 'Clear selection' : `Select all ${shown.value.length}${isNarrowed.value ? ' shown' : ''}`,
);

const pickedProperties = computed(() => properties.value.filter((property) => picked.value.includes(property.id)));
const selectedLabel = computed(() =>
  picked.value.length ? `${picked.value.length} selected` : 'Nothing selected yet',
);

// What the picks take out of other portfolios
const consequence = computed(() => {
  const moving = pickedProperties.value.filter((property) => property.portfolio);
  const from = new Set(moving.map((property) => property.portfolio!.name));

  if (!moving.length) {
    return '';
  }

  if (from.size === 1) {
    return `${moving.length} ${moving.length === 1 ? 'moves' : 'move'} from ${[...from][0]}`;
  }

  return `${moving.length} move from ${from.size} portfolios`;
});

const canCreate = computed(() => Boolean(fields.name.trim()) && Boolean(fields.ownerId));

function isPicked(property: PropertyListItemDto): boolean {
  return picked.value.includes(property.id);
}

function pick(property: PropertyListItemDto, checked: boolean) {
  picked.value = checked ? [...picked.value, property.id] : picked.value.filter((id) => id !== property.id);
}

/** A click anywhere on the row but the checkbox itself, which toggles on its own */
function onRowClick(property: PropertyListItemDto, event: MouseEvent) {
  if ((event.target as HTMLElement).closest('.checkbox')) {
    return;
  }

  pick(property, !isPicked(property));
}

/** Everything the search and the chip left on screen, in one go - or off again once they all are on */
function toggleShown() {
  const shownIds = shown.value.map((property) => property.id);

  picked.value = allShownPicked.value
    ? picked.value.filter((id) => !shownIds.includes(id))
    : [...new Set([...picked.value, ...shownIds])];
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function figuresOf(property: PropertyListItemDto): string {
  return propertySummary(property) || 'Not listed yet';
}

/** On a phone the status sits on this line, so only the rent fits beside it - or where the property comes from */
function shortFiguresOf(property: PropertyListItemDto): string {
  if (property.portfolio && isPicked(property)) {
    return 'Moves here';
  }

  return propertyRent(property);
}

/** Opens on everything, or narrowed to the loose properties when it stands in for filing them */
function open(filter: StatusFilter = 'all') {
  reset();
  fields.ownerId = investor.value?.id ?? '';
  statusFilter.value = filter;
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  Object.assign(fields, { name: '', ownerId: '' });
  picked.value = [];
  search.value = '';
  statusFilter.value = 'all';
  form.resetValidation();
}

async function create() {
  const name = fields.name.trim();
  const propertyIds = [...picked.value];
  let created: PortfolioDto | undefined;

  try {
    await form.submit(async () => {
      created = await createPortfolio.mutateAsync({
        name,
        ownerId: newPortfolioOwnerId(viewer.value, fields.ownerId),
        collaboratorIds: [],
        propertyIds,
      });
    });
  } catch {
    // A field the form or the server rejected shows on the field; the mutation toasted anything else
    return;
  }

  // The filed properties changed portfolio, so the page behind the panel re-reads them
  if (propertyIds.length) {
    queryCache.invalidateQueries({ key: [portfolioQueryKey] }).catch(() => undefined);
  }

  close();
  alertsStore.success(
    propertyIds.length
      ? `<b>${escapeHtml(name)}</b> created · ${pluralize('property', propertyIds.length, true)}`
      : `<b>${escapeHtml(name)}</b> created`,
  );

  if (created) {
    emit('created', created);
  }
}

defineExpose({ open });
</script>

<style lang="scss">
@import 'styles/mixins/layout.mixins';

// Teleported, so out of reach of scoped selectors: the body becomes a column that keeps its own
// height, and on a phone the footer stacks its summary over a full-width button
.modal-content.create-portfolio-panel {
  .modal-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  @include mobile {
    .modal-footer {
      flex-direction: column;
      gap: var(--t-spacing-sm);

      > :last-child {
        margin-inline-start: 0;
      }
    }
  }
}
</style>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--t-spacing-md);
  padding: var(--t-spacing-lg);
  border-bottom: 1px solid var(--t-color-border-secondary);

  @include mobile {
    grid-template-columns: minmax(0, 1fr);
    padding: var(--t-spacing-md);
  }
}

.owner-hint {
  grid-column: 1 / -1;

  // A phone has no room for it, and the picker says who the owner is
  @include mobile {
    display: none;
  }
}

.toolbar {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-sm);
  padding: var(--t-spacing-lg) var(--t-spacing-lg) var(--t-spacing-sm);

  @include mobile {
    padding: var(--t-spacing-md) var(--t-spacing-md) var(--t-spacing-sm);
  }
}

.search-icon {
  color: var(--t-text-color-tertiary);
}

.list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--t-spacing-xs) var(--t-spacing-lg) var(--t-spacing-md);

  @include mobile {
    padding: var(--t-spacing-xs) var(--t-spacing-md) var(--t-spacing-md);
  }
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--t-spacing-sm);
  padding: var(--t-spacing-md) var(--t-spacing-sm) var(--t-spacing-xs);

  &:first-child {
    padding-top: var(--t-spacing-sm);
  }
}

.group-title {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.group-note {
  flex-shrink: 0;

  // No room beside a long title on a phone; the group's own name already says where the property is
  @include mobile {
    display: none;
  }
}

.row {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-sm);
  padding: var(--t-spacing-sm);
  border-radius: var(--t-border-radius-sm);
  transition: background-color 150ms var(--t-ease-in-out);
  cursor: pointer;

  &:hover {
    background-color: var(--t-color-neutral-50);
  }

  &.checked {
    background-color: var(--t-color-neutral-100);
  }
}

// A long address has nowhere to break, so it wraps rather than pushing the row past the panel's width
.identity {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-xs);
  overflow-wrap: anywhere;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--t-spacing-sm);
}

// The status keeps the row's right edge on a desktop and moves under the address on a phone,
// where the figures give way to the rent alone
.status-inline,
.figures-short {
  display: none;
}

.status-trailing {
  white-space: nowrap;
}

@include mobile {
  .status-inline,
  .figures-short {
    display: inline-flex;
  }

  .status-trailing,
  .figures {
    display: none;
  }

  .row {
    min-height: 2.75rem;
    padding: var(--t-spacing-sm) var(--t-spacing-xs);
  }
}

.list-note {
  padding: var(--t-spacing-sm);
}

.summary {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-xs);
  min-width: 0;
}

.actions {
  display: flex;
  gap: var(--t-spacing-sm);
  align-items: center;
}

@include mobile {
  .summary {
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--t-spacing-sm);
  }

  .actions {
    width: 100%;
  }

  // The header's close is the way out on a phone; the one button left takes the whole width
  .cancel {
    display: none;
  }

  .create {
    width: 100%;
  }
}
</style>
